import { Children, cloneElement, useContext } from 'react';

import { useCalendarGetQuery } from '../../Common/Queries/calendarQueries';
import {
  useConfigGetQuery,
  useConfigPostQuery,
  useConfigPutQuery,
} from '../../Common/Queries/configQueries';
import { useDepartmentGetQuery } from '../../Common/Queries/departmentQueries';
import {
  useDeleteInterviewLocationMutationById,
  useInterviewLocationGetQuery,
} from '../../Common/Queries/interviewLocationQueries';
import { useLetterheadGetQuery } from '../../Common/Queries/letterheadQueries';
import {
  useDeleteLocationQueryById,
  useLocationGetQuery,
} from '../../Common/Queries/locationQueries';
import { useMasterInviteTemplateGetQuery } from '../../Common/Queries/masterInviteTemplateQueries';
import {
  useServicePostQuery,
  useServicePutRefreshTokensQuery,
} from '../../Common/Queries/serviceQueries';
import {
  useGetColorCode,
  useGetColorCodeMapping,
  userDeleteColorMapping,
} from '../../Common/Queries/settingQueries';

import { AuthContext, ROLES } from '../../constants';

export function SettingsContainer({ children }) {
  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.roles && userInfo?.roles[0];
  const child = Children.only(children);
  const inviteTemplate = useMasterInviteTemplateGetQuery();
  const departmentData = useDepartmentGetQuery();
  const locationData = useLocationGetQuery({ limit: 0 });
  const deleteLocationData = useDeleteLocationQueryById();
  const putConfig = useConfigPutQuery();
  const postConfig = useConfigPostQuery();
  const postServices = useServicePostQuery();
  const putRefrehToken = useServicePutRefreshTokensQuery();
  const deleteColorSetting = userDeleteColorMapping();
  const interviewLocationData = useInterviewLocationGetQuery();
  const deleteInterviewLocationData = useDeleteInterviewLocationMutationById();
  const calendarGetQuery = useCalendarGetQuery();

  const colorCodes = useGetColorCode({ enabled: ROLES.ADMIN === role });
  const backGroundColors = {};
  if (!colorCodes?.isLoading && colorCodes?.data)
    Object.keys(colorCodes?.data).forEach((key) => {
      backGroundColors[key] = colorCodes?.data?.[key]?.Background;
    });
  const configData = useConfigGetQuery('', {
    onSuccess: (successData) => {
      if (successData === null) {
        postConfig.mutate({});
      }
    },
  });
  const calendarColorData = useGetColorCodeMapping({ enabled: ROLES.ADMIN === role });
  const letterheadData = useLetterheadGetQuery({
    enabled:
      ROLES.SUPER_ADMIN === role ||
      ROLES.ADMIN === role ||
      ROLES.TENANT_OWNER === role ||
      ROLES.HR === role,
  });
  function handleDeleteLocationData(id) {
    const reqBody = locationData?.data?.data?.filter((location) => location.id === id);
    const reqParams = { reqBody, reqId: id };
    deleteLocationData.mutate(reqParams);
  }
  function handleDeleteInterviewLocationData(id) {
    const reqBody = interviewLocationData?.data?.data?.filter((location) => location.id === id);
    const reqParams = { reqBody, reqId: id };
    deleteInterviewLocationData.mutate(reqParams);
  }
  function handleDeletePositionScreenTime(key) {
    const updatedOverrides = configData?.data?.automaticScreeningTime?.overrides?.filter(
      ({ positionID }) => positionID !== key
    );

    const reqParams = {
      ...configData?.data,
      automaticScreeningTime: {
        ...configData?.data?.automaticScreeningTime,
        overrides: updatedOverrides,
      },
    };
    putConfig.mutate(reqParams);
  }

  function handleApplicationPutStatus(status) {
    const reqParams = { ...configData?.data, autoCloseDuplicateApplication: status };
    putConfig.mutate(reqParams);
  }

  function handleInterviewPutStatus(status) {
    const reqParams = {
      ...configData?.data,
      interviewConfig: {
        ...configData?.data?.interviewConfig,
        postStartUpdate: { ...configData?.data?.interviewConfig?.postStartUpdate, enabled: status },
      },
    };
    putConfig.mutate(reqParams);
  }

  function handlePutServices(refreshTokenData) {
    const reqParams = {
      ...configData?.data,
      refreshToken: refreshTokenData,
    };
    putConfig.mutate(reqParams);
  }

  function handlePutCalendarID(value) {
    const reqParams = {
      ...configData?.data,
      calendarID: value.name,
    };
    putConfig.mutate(reqParams);
  }

  function handleLetterheadUpload({ url }) {
    const reqParams = {
      ...configData?.data,
      letterhead: url,
    };
    putConfig.mutate(reqParams, {
      onSuccess: () => {
        letterheadData.refetch();
      },
    });
  }

  return cloneElement(child, {
    inviteTemplate,
    departmentData,
    locationData,
    interviewLocationData,
    deleteLocationData,
    handleDeleteLocationData,
    handleDeleteInterviewLocationData,
    handleDeletePositionScreenTime,
    configData,
    handleApplicationPutStatus,
    handleInterviewPutStatus,
    putConfig,
    handlePutCalendarID,
    postServices,
    handlePutServices,
    putRefrehToken,
    postConfig,
    calendarColorData,
    colorCodes: { ...colorCodes, data: backGroundColors },
    userInfo,
    deleteColorSetting,
    calendarGetQuery,
    letterheadData,
    handleLetterheadUpload,
  });
}
